/** One page of a list as the server sends it. */
export interface ListPage<T, S = undefined> {
  rows: T[];
  /** Every row the list counts, when it has a count (a ranking has none). */
  total?: number;
  /** Rows that can be paged through: the total, or the server's cap when it keeps fewer. */
  available: number;
  /** What the server sends beside the rows (counts by kind, unique players). */
  summary?: S;
}

/** The rows loaded so far, and where the next page starts. */
export interface PagedList<T, S = undefined> {
  rows: T[];
  /** From the first page, so it matches the summary shown with it. */
  total?: number;
  /** From the latest page. */
  available: number;
  /** From the first page. */
  summary?: S;
  /** Offset of the next page: rows the server has sent, repeats included. */
  next: number;
  /** A page came back empty, so there is nothing more to load whatever `available` says. */
  ended: boolean;
}

/**
 * Adds a page (the first when `list` is null). A row seen before is not added again: a list that changes
 * between two pages (a new flag at the top, a refreshed cache) shifts the server's offsets by a row or two.
 */
export function addPage<T, S>(
  list: PagedList<T, S> | null,
  page: ListPage<T, S>,
  key: (row: T) => string
): PagedList<T, S> {
  const seen = new Set((list?.rows ?? []).map(key));
  const fresh = page.rows.filter((row) => {
    const k = key(row);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return {
    rows: [...(list?.rows ?? []), ...fresh],
    total: list ? list.total : page.total,
    available: page.available,
    summary: list ? list.summary : page.summary,
    next: (list?.next ?? 0) + page.rows.length,
    ended: page.rows.length === 0,
  };
}

export const hasMore = (list: PagedList<unknown, unknown> | null): boolean =>
  !!list && !list.ended && list.next < list.available;
